/**
 * 원장 — **모든 돈이 여기 한 줄씩.** 이 모델에서 제일 중요하다.
 *
 * 축이 셋이다 — 자금원(항상) · 건(투자·회수) · 참여자(출자·분배·보수).
 * 그래서 같은 원장에서 세 가지 수익률이 나온다.
 *
 * ⚠️ 금액에 **부호를 쓰지 않는다.** 방향은 `direction` 이 갖는다.
 * ⚠️ `occurredOn` 은 **통장 입출금일**이다. 계약일이 아니다 — 수익률이 날짜로 계산된다.
 */
export interface Cashflow {
  id: number;
  /** 어느 지갑. 항상 있다 */
  vehicleId: number;
  dealId: number | null;
  participantId: number | null;
  direction: Direction;
  kind: FlowKind;
  /** 보수일 때만 — mgmt 관리보수 · carry 성과보수. LP 보고가 둘을 가른다 */
  feeKind: FeeKind | null;
  amount: number;
  occurredOn: string;
  memo: string;
}

export const FEE_KINDS = ['mgmt', 'carry'] as const;
export type FeeKind = (typeof FEE_KINDS)[number];
export const FEE_LABEL: Record<FeeKind, string> = { mgmt: '관리보수', carry: '성과보수' };

export const DIRECTIONS = ['in', 'out'] as const;
export type Direction = (typeof DIRECTIONS)[number];

export const FLOW_KINDS = [
  'contribution',
  'exit',
  'dividend',
  'investment',
  'distribution',
  'fee',
  'expense',
] as const;
export type FlowKind = (typeof FLOW_KINDS)[number];

interface KindMeta {
  label: string;
  direction: Direction;
  /** 무엇을 같이 골라야 하는가 */
  needs: 'deal' | 'participant' | 'none';
  note: string;
  style: string;
}

export const KIND_META: Record<FlowKind, KindMeta> = {
  contribution: {
    label: '출자', direction: 'in', needs: 'participant',
    note: '참여자가 자금원에 넣은 돈 (수익률 계산의 시작일)',
    style: 'bg-sky-50 text-sky-700',
  },
  exit: {
    label: '회수', direction: 'in', needs: 'deal',
    note: '투자처에서 돌아온 돈 (분배 전까지 자금원에 남음)',
    style: 'bg-emerald-50 text-emerald-700',
  },
  dividend: {
    label: '배당·이자', direction: 'in', needs: 'deal',
    note: '보유 중에 들어온 돈',
    style: 'bg-emerald-50 text-emerald-700',
  },
  investment: {
    label: '투자', direction: 'out', needs: 'deal',
    note: '자금원에서 투자처로 나간 돈',
    style: 'bg-indigo-50 text-indigo-600',
  },
  distribution: {
    label: '분배', direction: 'out', needs: 'participant',
    note: '참여자에게 돌려준 돈',
    style: 'bg-violet-50 text-violet-700',
  },
  fee: {
    label: '보수', direction: 'out', needs: 'participant',
    note: 'GRK 가 받은 관리보수 · 성과보수 (GRK 매출)',
    style: 'bg-amber-50 text-amber-700',
  },
  expense: {
    label: '비용', direction: 'out', needs: 'none',
    note: '운용에 든 비용',
    style: 'bg-slate-100 text-slate-600',
  },
};

export interface CashflowForm {
  vehicleId: number | null;
  kind: FlowKind;
  dealId: number | null;
  participantId: number | null;
  feeKind: FeeKind;
  amount: number | null;
  occurredOn: string;
  memo: string;
}
